"use client";

import { useCallbackRef } from "@dc/hooks";
import { Autocomplete, AutocompleteItem } from "@heroui/react";
import type { Key } from "@react-types/shared";
import type { FC } from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import type { AutocompleteWithAddProps } from "./AutocompleteWithAdd.types";

export const ADD_ITEM_KEY = "__add_new_item__";
export const EMPTY_PLACEHOLDER_KEY = "__empty_placeholder__";

export const AutocompleteWithAdd: FC<AutocompleteWithAddProps> = ({
  items,
  onAddItem,
  onSelectionChange,
  label,
  placeholder,
  isDisabled,
  isLoading,
  isInvalid,
  isRequired,
  errorMessage,
  description,
  className,
  addOptionPrefix = "Create",
  defaultSelectedKey,
  selectedOption,
  onInputChange,
  inputValue,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  // Hero UI autocomplete fires a null selection event after the item is created.
  //This is used to skip the null selection event that is fired after the item is created
  const hasJustCreatedItemRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Prevent Tab from triggering item creation via React Aria's useComboBox.
   * React Aria registers native listeners directly on the input, which fire
   * during the bubble phase — BEFORE React's root delegation dispatches
   * synthetic events. An `onKeyDown` prop on this wrapper would fire too late
   * (after state.commit() has already been called).
   *
   * Using a native capture-phase listener on the wrapper fires top-down,
   * before React Aria's input listener ever sees the event. stopPropagation
   * (not preventDefault) stops the event reaching the input while still
   * allowing the browser's built-in Tab focus movement to work.
   */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Tab") e.stopPropagation();
    };
    el.addEventListener("keydown", onKeyDown, { capture: true });
    return () => el.removeEventListener("keydown", onKeyDown, { capture: true });
  }, []);

  const onInputChangeRef = useCallbackRef(onInputChange);

  useLayoutEffect(() => {
    onInputChangeRef.current(selectedOption?.label || "");
  }, [selectedOption?.label]);

  const handleSelectionChange = async (key: Key | null) => {
    if (key !== null && hasJustCreatedItemRef.current) {
      hasJustCreatedItemRef.current = false;
    }

    //don't fire the null selection event if the item is just created
    if (key === null && hasJustCreatedItemRef.current) {
      hasJustCreatedItemRef.current = false;
      return;
    }

    if (isAdding && key === null) {
      return;
    }
    // Ignore clicks on the placeholder
    if (key === EMPTY_PLACEHOLDER_KEY) return;
    if (key === ADD_ITEM_KEY && onAddItem) {
      const trimmedInput = inputValue.trim();
      if (!trimmedInput) return;

      setIsAdding(true);
      try {
        const newItem = await onAddItem(trimmedInput);
        if (newItem) {
          hasJustCreatedItemRef.current = true;
          onInputChange(newItem.label);
          onSelectionChange?.(newItem.key, newItem);
        } else {
          // Creation failed - clear input and don't select anything
          onInputChange("");
        }
      } finally {
        setIsAdding(false);
      }
    } else if (key !== ADD_ITEM_KEY) {
      // Existing item selected or cleared
      const stringKey = key as string | null;
      const selectedItem = stringKey ? (items.find((item) => item.key === stringKey) ?? null) : null;

      onSelectionChange?.(stringKey, selectedItem);
      onInputChange(selectedItem?.label || "");
    }
  };

  /**
   * If user blurs the input before they have selected an option, reset the
   * value so that they don't assume the client was created or selected
   */
  const resetInputValue = useCallback(() => {
    if (!selectedOption) onInputChange("");
  }, [onInputChange, selectedOption]);

  return (
    <div ref={containerRef}>
      <Autocomplete
        className={cn(className)}
        label={label}
        placeholder={placeholder}
        isDisabled={isDisabled || isAdding}
        isLoading={isLoading || isAdding}
        isInvalid={isInvalid}
        isRequired={isRequired}
        validationBehavior="aria"
        errorMessage={errorMessage}
        description={description}
        defaultSelectedKey={defaultSelectedKey}
        selectedKey={selectedOption?.key || null}
        items={items}
        inputValue={inputValue}
        onInputChange={(val) => {
          if (val === "" && !isAdding) {
            handleSelectionChange(null);
          }
          onInputChange(val);
        }}
        onSelectionChange={handleSelectionChange}
        allowsCustomValue
        clearButtonProps={{ "aria-label": "Clear input" }}
        allowsEmptyCollection
        menuTrigger="focus"
        disabledKeys={[EMPTY_PLACEHOLDER_KEY]}
        onBlur={resetInputValue}
      >
        {(item) =>
          item.key === EMPTY_PLACEHOLDER_KEY ? (
            <AutocompleteItem key={item.key} textValue={item.label} isReadOnly>
              {item.label}
            </AutocompleteItem>
          ) : item.key === ADD_ITEM_KEY ? (
            <AutocompleteItem key={item.key} textValue={item.label}>
              <span>{`${addOptionPrefix} "${inputValue.trim()}"`}</span>
            </AutocompleteItem>
          ) : (
            <AutocompleteItem key={item.key}>{item.label}</AutocompleteItem>
          )
        }
      </Autocomplete>
    </div>
  );
};
